import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { addDays, endOfMonth, startOfMonth } from 'date-fns';
import { trpc } from '@org/clients/school';
import { toast } from 'sonner';
import { Button, GenericModal, ProtectedRoute } from '@org/ui';
import { EventCalendarAdmin } from '../app/components/events/event-calendar';
import { EventForm } from '../app/components/events/event-form';
import { useTranslation } from 'react-i18next';


type ModalState =
  | { mode: 'create'; anchorDate?: Date }
  | { mode: 'edit'; eventId: string };

export const Route = createFileRoute('/events')({
  component: EventsRoute,
});

function EventsRoute() {
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [modalState, setModalState] = useState<ModalState | null>(null);
  const { t } = useTranslation('admin');

  const rangeStart = useMemo(() => addDays(startOfMonth(currentMonth), -7), [currentMonth]);
  const rangeEnd = useMemo(() => addDays(endOfMonth(currentMonth), 7), [currentMonth]);

  const eventsQuery = trpc.events.list.useQuery({
    start: rangeStart,
    end: rangeEnd,
  });

  const deleteMutation = trpc.events.delete.useMutation({
    onSuccess: () => {
      eventsQuery.refetch();
      toast.success(t('common.toasts.deleteSuccess', { entity: 'Event' }));
    },
    onError: (error) => {
      // Check if error message is a translation key
      const errorMessage = error.message;
      toast.error(t(errorMessage) || t('common.toasts.actionError', { action: 'delete', entity: 'event' }));
    },
  });

  const handleDeleteEvent = async (eventId: string, deleteFutureOccurrences: boolean) => {
    try {
      await deleteMutation.mutateAsync({ eventId, deleteFutureOccurrences });
    } catch (error) {
      console.error('Failed to delete event', error);
    }
  };

  const events = useMemo(() => {
    return (eventsQuery.data ?? []).map((event) => ({
      eventId: event.eventId,
      title: event.title,
      startTime: event.startTime,
      endTime: event.endTime,
      eventKind: event.eventKind as 'class' | 'open' | 'semi-private',
      status: event.status as 'scheduled' | 'cancelled' | 'completed' | 'draft',
      isTemplate: event.isTemplate,
      seriesTemplateId: event.seriesTemplateId,
      category: event.category ?? undefined,
    }));
  }, [eventsQuery.data]);

  const selectedEventId = modalState?.mode === 'edit' ? modalState.eventId : undefined;
  const eventDetailQuery = trpc.events.get.useQuery(selectedEventId!, {
    enabled: modalState?.mode === 'edit' && Boolean(selectedEventId),
  });

  const handleCloseModal = () => setModalState(null);

  const handleSuccess = () => {
    setModalState(null);
  };

  const modalBody = useMemo(() => {
    if (!modalState) return null;

    if (modalState.mode === 'edit' && eventDetailQuery.isLoading) {
      return (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('common.status.loadingDetails')}
        </div>
      );
    }

    return (
      <EventForm
        mode={modalState.mode}
        defaultEvent={modalState.mode === 'edit' ? eventDetailQuery.data ?? null : null}
        anchorDate={modalState.mode === 'create' ? modalState.anchorDate : undefined}
        onSuccess={handleSuccess}
        onClose={handleCloseModal}
      />
    );
  }, [modalState, eventDetailQuery.data, eventDetailQuery.isLoading]);

  return (
    <ProtectedRoute requiredPermissions={['admin:all', 'events:*', 'classes:*']}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">{t('events.page.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('events.page.subtitle')}</p>
          </div>
          <Button variant="outline" onClick={() => setModalState({ mode: 'create' })}>{t('events.page.createButton')}</Button>
        </div>

        <EventCalendarAdmin
          month={currentMonth}
          onMonthChange={(date) => setCurrentMonth(startOfMonth(date))}
          events={events}
          onSelectEvent={(eventId) => setModalState({ mode: 'edit', eventId })}
          onCreateEvent={(date) => setModalState({ mode: 'create', anchorDate: date })}
          onDeleteEvent={handleDeleteEvent}
        />
      </div>

      {modalState && (
        <GenericModal
          show
          onClose={handleCloseModal}
          title={modalState.mode === 'create' ? t('events.modal.createTitle') : t('events.modal.editTitle')}
          size="xl"
          centered
          body={modalBody}
          footer={<></>}
        />
      )}
    </ProtectedRoute>
  );
}

